import { booleanAttribute, ChangeDetectionStrategy, Component, input, model, output } from '@angular/core';
import { DsButtonModule, DsIconModule, DsMessageModule, SelectListOption } from '@bmw-ds/components';

import { SuggestedAddress } from '../../../clients/tolerant-post/model/view.model';
import { AddressField } from '../../../customer/model/address-field.model';
import { CustomerAddress } from '../../../customer/model/customer-information.model';
import { SuggestionOfAddressField } from '../../../customer/services/address.view-service';
import { useI18n } from '../../../i18n/services/i18n.service';
import { RegionalAddressRowComponent } from './regional-address-row.component';

@Component({
  selector: 'cdf-regional-address',
  imports: [RegionalAddressRowComponent, DsButtonModule, DsMessageModule, DsIconModule],
  template: `
    @for (addressRow of addressUiFormat(); track addressRow[0]) {
      <cdf-regional-address-row
        [(address)]="address"
        [addressRow]="addressRow"
        [availableCountries]="availableCountries()"
        [availableProvinces]="availableProvinces()"
        [disabled]="disabled()"
        [disableIfNoCountry]="disableIfNoCountry()"
        [suggestions]="suggestions()"
        [isClearable]="isClearable()"
        [invalidAddressFields]="invalidAddressFields()"
        (selectedSuggestionForAddressField)="selectedSuggestionForAddressField.emit($event)"
      />
    }

    @if (invalidAddressFields().length > 0) {
      <div class="caution">
        <ds-message tone="caution" variant="slim" [isDismissable]="false" [messageText]="addressWarningTemplate" />
        <ng-template #addressWarningTemplate>
          <div class="address-warning-template">
            <span class="address-warning-message">{{ i18n('pleaseSelectInputSuggestion') }}</span>
            <ds-button variant="outline" size="sm" (click)="skipAddressValidation.emit()"
              >{{ i18n('button.skipAddressValidation') }}
            </ds-button>
          </div>
        </ng-template>
      </div>
    }

    @if (displayClearAddressButton()) {
      <div class="clear-address-button">
        <ds-button variant="outline" (click)="clearAddressInformation()" [disabled]="disabled()">
          {{ i18n('button.clearAddressInformation') }}
        </ds-button>
      </div>
    }
  `,
  styleUrl: './regional-address.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegionalAddressComponent {
  readonly i18n = useI18n();

  readonly address = model.required<CustomerAddress | undefined>();
  readonly addressUiFormat = input.required<AddressField[][]>();

  readonly suggestions = input<SuggestedAddress[] | undefined>(undefined);
  readonly availableCountries = input.required<SelectListOption[]>();
  readonly availableProvinces = input<SelectListOption[]>([]);
  readonly isClearable = input(false, { transform: booleanAttribute });
  readonly disabled = input(false);

  readonly invalidAddressFields = input<AddressField[]>([]);

  // Disable all other input fields, if not country is set.
  readonly disableIfNoCountry = input(false, { transform: booleanAttribute });
  readonly displayClearAddressButton = input(true);

  readonly selectedSuggestionForAddressField = output<SuggestionOfAddressField>();
  readonly skipAddressValidation = output<void>();

  clearAddressInformation() {
    this.address.set({
      country: '',
      postalCode: '',
      city: '',
      houseNumber: '',
      street: '',
      province: '',
      addressLine1: '',
      addressLine2: '',
      postBox: '',
    } satisfies Required<CustomerAddress>);
  }
}
